import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {TabPanel, TabView} from "primereact/tabview";
import {CONTENT_TYPE_UIDS} from "../../../system/content-type-registry";
import {hasAdminPermission} from "../../../system/admin-access";
import {useSingleTypeData} from "../../../system/use-single-type-data";
import {useTranslate} from "../../../system/useTranslate";
import MarkdownHelp from "../../../system/components/markdown-help";
import BillingCollectionTable, {dateColumn, relationColumn, statusColumn} from "../components/billing-collection-table";
import {FormField, RTL_DROPDOWN_PROPS, SalesSectionHeader, SwitchField} from "../components/common";

const DEFAULTS = {
    enabled: false,
    attributionWindowDays: 30,
    qualificationEvent: "first_payment",
    qualificationDelayDays: 0,
    minimumQualifiedPayment: "0",
    pointsPerQualifiedReferral: "0",
    minimumRedeemPoints: "0",
    pointToDiscountRatio: 1,
    maximumDiscountPerRedemption: null,
    rewardPendingDays: 0,
    pointsExpireAfterDays: null,
    discountValidityDays: 30,
    referredRewardEnabled: false,
    referredRewardType: "none",
    referredRewardValue: 0,
    selfReferralBlocked: true,
    duplicateIdentityPolicy: "reject",
    fraudReviewEnabled: true,
    terms: "",
};

const options = (values) => values.map((value) => ({label: value, value}));

function ReferralSettings() {
    const translate = useTranslate();
    const {user} = useSelector((state) => state.auth);
    const canManage = hasAdminPermission(user, "referrals.settings.manage");
    const settings = useSingleTypeData(CONTENT_TYPE_UIDS.REFERRAL_SETTING);
    const [form, setForm] = useState(DEFAULTS);
    const [notice, setNotice] = useState(null);

    useEffect(() => {
        if (settings.item) setForm({...DEFAULTS, ...settings.item});
    }, [settings.item]);

    const save = async () => {
        if (!canManage) return;
        setNotice(null);
        try {
            await settings.save({
                ...form,
                minimumQualifiedPayment: String(form.minimumQualifiedPayment || 0),
                pointsPerQualifiedReferral: String(form.pointsPerQualifiedReferral || 0),
                minimumRedeemPoints: String(form.minimumRedeemPoints || 0),
                maximumDiscountPerRedemption: form.maximumDiscountPerRedemption == null ? null : String(form.maximumDiscountPerRedemption),
            });
            setNotice({severity: "success", text: translate("billing-admin-referral-settings-saved")});
        } catch (error) {
            setNotice({severity: "error", text: error?.response?.data?.error?.message || error?.message});
        }
    };

    return (
        <div className="flex flex-column gap-3">
            <SalesSectionHeader title={translate("billing-admin-referral-settings")} description={translate("billing-admin-referral-settings-description")}/>
            {notice && <Message className="w-full" severity={notice.severity} text={notice.text}/>}
            {settings.error && <Message className="w-full" severity="error" text={settings.error}/>}
            <div className="grid">
                <SwitchField label={translate("billing-admin-enabled")} checked={form.enabled} onChange={(value) => setForm({...form, enabled: value})}/>
                <FormField label={translate("billing-admin-attribution-days")}><InputNumber className="w-full" value={form.attributionWindowDays} min={1} onValueChange={(event) => setForm({...form, attributionWindowDays: event.value})}/></FormField>
                <FormField label={translate("billing-admin-qualification-event")}><Dropdown {...RTL_DROPDOWN_PROPS} value={form.qualificationEvent} options={options(["registration", "workspace_created", "trial_started", "first_payment", "minimum_payment", "subscription_active_for_days"])} onChange={(event) => setForm({...form, qualificationEvent: event.value})}/></FormField>
                <FormField label={translate("billing-admin-qualification-delay")}><InputNumber className="w-full" value={form.qualificationDelayDays} min={0} onValueChange={(event) => setForm({...form, qualificationDelayDays: event.value})}/></FormField>
                <FormField label={translate("billing-admin-minimum-payment")}><InputNumber className="w-full" value={Number(form.minimumQualifiedPayment)} min={0} useGrouping={false} onValueChange={(event) => setForm({...form, minimumQualifiedPayment: event.value})}/></FormField>
                <FormField label={translate("billing-admin-points-per-referral")}><InputNumber className="w-full" value={Number(form.pointsPerQualifiedReferral)} min={0} useGrouping={false} onValueChange={(event) => setForm({...form, pointsPerQualifiedReferral: event.value})}/></FormField>
                <FormField label={translate("billing-admin-minimum-redeem")}><InputNumber className="w-full" value={Number(form.minimumRedeemPoints)} min={0} useGrouping={false} onValueChange={(event) => setForm({...form, minimumRedeemPoints: event.value})}/></FormField>
                <FormField label={translate("billing-admin-point-ratio")}><InputNumber className="w-full" value={Number(form.pointToDiscountRatio)} min={0} maxFractionDigits={4} onValueChange={(event) => setForm({...form, pointToDiscountRatio: event.value})}/></FormField>
                <FormField label={translate("billing-admin-reward-pending-days")}><InputNumber className="w-full" value={form.rewardPendingDays} min={0} onValueChange={(event) => setForm({...form, rewardPendingDays: event.value})}/></FormField>
                <FormField label={translate("billing-admin-discount-validity-days")}><InputNumber className="w-full" value={form.discountValidityDays} min={1} onValueChange={(event) => setForm({...form, discountValidityDays: event.value})}/></FormField>
                <SwitchField label={translate("billing-admin-block-self-referral")} checked={form.selfReferralBlocked} onChange={(value) => setForm({...form, selfReferralBlocked: value})}/>
                <SwitchField label={translate("billing-admin-fraud-review")} checked={form.fraudReviewEnabled} onChange={(value) => setForm({...form, fraudReviewEnabled: value})}/>
                <FormField className="col-12" label={translate("billing-admin-terms")}><InputTextarea className="w-full text-xs" rows={4} value={form.terms || ""} onChange={(event) => setForm({...form, terms: event.target.value})}/></FormField>
            </div>
            {canManage && <Button className="w-full text-xs gap-2" icon="pi pi-save" label={translate("save")} loading={settings.saving} onClick={save}/>}
            <MarkdownHelp contentKey="billing-admin-referral-settings-help"/>
        </div>
    );
}

export default function ReferralsTab() {
    const translate = useTranslate();
    const {user} = useSelector((state) => state.auth);
    const canView = hasAdminPermission(user, "referrals.view");
    const canManageSettings = hasAdminPermission(user, "referrals.settings.manage");
    const canViewRedemptions = hasAdminPermission(user, "referrals.redemptions.view");
    return (
        <TabView className="rtl text-xs">
            {(canView || canManageSettings) && <TabPanel header={translate("billing-admin-referral-settings")}><ReferralSettings/></TabPanel>}
            {canView && <TabPanel header={translate("billing-admin-referral-accounts")}>
                <BillingCollectionTable uid={CONTENT_TYPE_UIDS.REFERRAL_ACCOUNT} titleKey="billing-admin-referral-accounts" columns={[
                    relationColumn("user", "sales-user"),
                    {field: "referralCode", headerKey: "billing-admin-code"},
                    {field: "availablePoints", headerKey: "billing-admin-available-points"},
                    statusColumn("referralAccountStatus"),
                ]}/>
            </TabPanel>}
            {canView && <TabPanel header={translate("billing-admin-referral-events")}>
                <BillingCollectionTable uid={CONTENT_TYPE_UIDS.REFERRAL_EVENT} titleKey="billing-admin-referral-events" columns={[
                    relationColumn("referralAccount", "billing-admin-referral-account"),
                    {field: "type", headerKey: "billing-admin-event-type"},
                    {field: "points", headerKey: "billing-admin-points"},
                    statusColumn("referralEventStatus"),
                    dateColumn("occurredAt"),
                ]}/>
            </TabPanel>}
            {canViewRedemptions && <TabPanel header={translate("billing-admin-reward-redemptions")}>
                <BillingCollectionTable uid={CONTENT_TYPE_UIDS.REWARD_REDEMPTION} titleKey="billing-admin-reward-redemptions" columns={[
                    relationColumn("user", "sales-user"),
                    {field: "pointsSpent", headerKey: "billing-admin-points-spent"},
                    relationColumn("discountCode", "billing-admin-code"),
                    statusColumn("rewardRedemptionStatus"),
                    dateColumn("requestedAt"),
                ]}/>
            </TabPanel>}
        </TabView>
    );
}
